<template>
  <div id="application-logs" class="stretch">
    <el-row class="filters" :gutter="0">
      <el-col :span="7">
        <el-input
          v-model="form.query"
          size="medium"
          prefix-icon="el-icon-search"
          placeholder="Search"
          @change="load"
        />
      </el-col>

      <el-col class="align-right" :span="17">
        <el-date-picker
          v-model="form.date"
          type="daterange"
          size="small"
          align="right"
          unlink-panels
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :picker-options="pickerOptions"
          @change="load"
        />

        <el-button
          type="primary"
          size="small"
          icon="el-icon-download"
          class="download"
          @click.prevent="download"
        >
          Download
        </el-button>

        <span :class="{ live }">
          Live
          <el-switch v-model="live" active-color="#ff4949" />
        </span>
      </el-col>
    </el-row>

    <el-table
      v-loading="loading"
      ref="logs"
      :data="data"
      :span-method="span"
      :row-class-name="severity"
      size="mini"
      @row-click="open"
    >
      <el-table-column type="expand">
        <template slot-scope="scope">
          <p v-if="scope.row.request">
            <el-link :href="link(scope.row)" target="_blank">{{ link(scope.row) }}</el-link>
          </p>
          <div v-if="scope.row.ip">Origin : {{ scope.row.ip }}</div>
          <div v-if="scope.row.user_agent">User Agent : {{ scope.row.user_agent }}</div>
          <div v-if="scope.row.referer">Referer : {{ scope.row.referer }}</div>
          <div v-if="scope.row.user">User : {{ scope.row.user }}</div>
          <div v-if="scope.row.bytes">Bytes sent : {{ filesize(scope.row.bytes) }}</div>
        </template>
      </el-table-column>

      <el-table-column prop="@timestamp" label="Date" width="150px" sortable>
        <template slot-scope="scope">
          {{ toCalendar(moment(scope.row['@timestamp']).unix()) }}
        </template>
      </el-table-column>

      <el-table-column label="Domain" :show-overflow-tooltip="true" width="240px">
        <template slot-scope="scope">
          <el-button type="text" size="small">{{ scope.row.domain }}</el-button>
        </template>
      </el-table-column>

      <el-table-column label="Log">
        <template slot-scope="scope">
            <span v-if="scope.row.request">
              <el-tag
                :type="status(scope.row.status)"
                :disable-transitions="true"
                size="mini"
              >
                {{ scope.row.status }}
              </el-tag>

              {{ scope.row.method }} {{ scope.row.request }}
            </span>
          <span v-else>{{ scope.row.error }}</span>
        </template>
      </el-table-column>

      <el-table-column prop="time" label="Time" width="120px">
        <template slot-scope="scope">
          <span v-if="scope.row.time">{{ scope.row.time }}</span>
        </template>
      </el-table-column>

    </el-table>

    <Pagination :total="total" :page.sync="form.page" :per-page.sync="form.size" />
  </div>
</template>

<script>
import moment from 'moment-timezone'
import filesize from 'filesize'
import Pagination from '../Pagination'

export default {
  props: {
    instance: Object,
    application: Object
  },
  components: {
    Pagination
  },
  data () {
    return {
      moment,
      filesize,
      data: [],
      total: 0,
      event: null,
      loading: false,
      live: true,
      form: {
        id: this.instance.id,
        app: this.application.name,
        date: [],
        query: '',
        page: 1,
        size: 25
      },
      pickerOptions: {
        shortcuts: [{
          text: 'Last 15 min',
          onClick (picker) {
            picker.$emit('pick', [moment().subtract(15, 'minute').format(), moment().format()])
          }
        }, {
          text: 'Last 1 hour',
          onClick (picker) {
            picker.$emit('pick', [moment().subtract(1, 'hour').format(), moment().format()])
          }
        }, {
          text: 'Last 4 hours',
          onClick (picker) {
            picker.$emit('pick', [moment().subtract(4, 'hour').format(), moment().format()])
          }
        }, {
          text: 'Last 12 hours',
          onClick (picker) {
            picker.$emit('pick', [moment().subtract(12, 'hour').format(), moment().format()])
          }
        }, {
          text: 'Last 24 hours',
          onClick (picker) {
            picker.$emit('pick', [moment().subtract(24, 'hour').format(), moment().format()])
          }
        }, {
          text: 'Last 7 days',
          onClick (picker) {
            picker.$emit('pick', [moment().subtract(7, 'day').format(), moment().format()])
          }
        }, {
          text: 'Today',
          onClick (picker) {
            picker.$emit('pick', [moment().set({ hour: 0, minute: 0, second: 0 }).format(), moment().format()])
          }
        }, {
          text: 'This week',
          onClick (picker) {
            picker.$emit('pick', [moment().set({ day: 1, hour: 0, minute: 0, second: 0 }).format(), moment().format()])
          }
        }, {
          text: 'This month',
          onClick (picker) {
            picker.$emit('pick', [moment().date(1).set({ hour: 0, minute: 0, second: 0 }).format(), moment().format()])
          }
        }]
      }
    }
  },
  created () {
    this.load()
    this.connect()
  },
  methods: {
    load () {
      if (this.loading) {
        return
      }

      this.loading = true
      this.form.id = this.instance.id
      this.form.app = this.application.name
      this.$store.dispatch('getInstanceLogs', this.form).then((res) => {
        this.data = res.data
        this.total = res.total
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    download () {
      this.$store.dispatch('getInstanceLogs', { ...this.form, download: true }).then(() => {
        this.$notify({
          type: 'success',
          title: 'Export in progress',
          message: 'Your export is being generated. You will receive it by email shortly.'
        })
      })
    },
    status (type) {
      if (type >= 400) {
        return 'danger'
      } else if (type >= 300) {
        return 'warning'
      } else {
        return 'success'
      }
    },
    link (row) {
      let url = ''
      if (row.scheme) {
        url += row.scheme + '://' + row.domain
      }
      if (row.request) {
        url += row.request
      }
      return url
    },
    severity ({ row }) {
      if (row.severity === 'err' || row.severity === 'error' || row.status >= 500) {
        return 'error'
      }
      if (row.severity === 'warn' || row.severity === 'warning' || row.status >= 400) {
        return 'warning'
      }
      return ''
    },
    span ({ row, columnIndex }) {
      if (columnIndex === 3) {
        if (row.error) {
          return [1, 2]
        } else {
          return [1, 1]
        }
      } else if (columnIndex > 3) {
        if (row.error) {
          return [0]
        }
      }
    },
    open (row) {
      this.$refs.logs.toggleRowExpansion(row)
    },
    connect () {
      if (this.live) {
        const today = moment().format('YYYYMMDD')
        this.event = this.$sse.channel(this.instance.id + '/proxy-' + this.application.name)
        this.event.addEventListener('message', (ev) => {
          if (this.form.query || this.form.page > 1 || (Array.isArray(this.form.date) && this.form.date.length > 1 &&
            moment(this.form.date[1]).startOf('day').format('YYYYMMDD') !== today)) {
            return
          }
          try {
            const json = JSON.parse(ev.data)
            this.data.unshift(json)
            this.data.splice(this.form.size)
          } catch (e) {
          }
        })
      } else if (this.event) {
        this.event.close()
        this.event = null
      }
    }
  },
  beforeDestroy () {
    if (this.event) {
      this.event.close()
      this.event = null
    }
  },
  watch: {
    'form.page' () {
      this.$nextTick(() => this.load())
      this.$refs.logs.$el.scrollIntoView()
    },
    'form.size' () {
      this.$nextTick(() => this.load())
      this.$refs.logs.$el.scrollIntoView()
    },
    live () {
      this.connect()
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../theme";

  .filters {
    padding-left: 24px;
    padding-right: 24px;
  }
  .el-button {
    margin-left: 10px;
    position: relative;
    top: -1px;
  }
  .el-table {
    margin-top: 12px;
    margin-bottom: -1px;
  }
  .download {
    margin-right: 5px;
  }
</style>

<style lang="scss">
  #application-logs {
    .el-input {
      input {
        border-radius: 0;
        border-top: none;
        border-left: none;
        border-right: none;
        background-color: transparent;
      }
    }
    .el-table {
      .error, .error:hover {
        background: #fceae4;
      }
      .warning {
        background: #fef2de;
      }
      .el-table__row {
        cursor: pointer;
      }
      .el-button--text {
        padding-left: 0;
        padding-right: 0;
        margin: 0;
      }
    }
  }
</style>
